import type { ProjectUndeliveredFamilies } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';
import { formatDateTime, formatRelativeTime } from '../../lib/dateFormat';
import {
  formatFailureCount,
  listUndeliveredFamilies,
  type UndeliveredFamilyEntry,
} from './undeliveredFamilyMeta';

function UndeliveredFamilyRow({ entry }: { entry: UndeliveredFamilyEntry }) {
  const { label, detail, meta } = entry;

  return (
    <div className="flex flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/40">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">{label}</span>
        <span title={meta.meaning}>
          <Badge color={meta.color}>{meta.label}</Badge>
        </span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          {formatFailureCount(detail.failures)}
        </span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400" title={formatDateTime(detail.firstSeenAt)}>
          first seen {formatRelativeTime(detail.firstSeenAt)}
        </span>
      </div>
      <div className="text-xs break-words text-gray-700 dark:text-zinc-300">{detail.message}</div>
      {detail.path && (
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          Refusing field:{' '}
          <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200">
            {detail.path}
          </code>
        </div>
      )}
    </div>
  );
}

/**
 * Full per-family breakdown of what a project still owes Oil (DR-2855).
 * Renders nothing when nothing is owed.
 */
export function UndeliveredFamilyList({ families }: { families?: ProjectUndeliveredFamilies | null }) {
  const entries = listUndeliveredFamilies(families);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {entries.map((entry) => (
        <UndeliveredFamilyRow key={entry.family} entry={entry} />
      ))}
    </div>
  );
}
